import { styled } from "goober";
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ChatTabContextMenu } from "./ChatTabContextMenu";
import { ChatTabsDropdown } from "./ChatTabsDropdown";
import { readOnlyStyle } from "./readOnlyStyle";
import { pickChatIconColor } from "./pickChatIconColor";
import { mobileMedia, useIsMobile } from "../../hooks/useIsMobile";
import { playClick } from "../../lib/sounds";
import { useMakeUrl, routes } from "../../lib/urls";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { NudgeTooltipContent } from "../NudgeTooltipContent";

type TabChat = {
  id: string;
  name: string;
  lastActivityAt: number | null;
  pinned?: boolean;
  // Effective sort position, used to compute a new position when reordering.
  position?: number;
  // Unsaved new-chat placeholder: rendered at the end and not draggable.
  pending?: boolean;
  // Whether the chat has entries; gates the started-only context-menu actions.
  started?: boolean;
};

type Props = {
  tabs: readonly TabChat[];
  activeTabId: string | null;
  userId?: string;
  closedChats: readonly {
    id: string;
    name: string;
    closedAt: number;
    lastActivityAt: number;
  }[];
  onClose?: ((id: string) => void) | undefined;
  onRename?: ((id: string) => void) | undefined;
  onFork?: ((id: string) => void) | undefined;
  onDownload?: ((id: string) => void) | undefined;
  onTogglePin?: ((id: string) => void) | undefined;
  onNewTab?: (() => void) | undefined;
  onTidyUp?: (() => void) | undefined;
  onCloseAll?: (() => void) | undefined;
  onShowHistory?: (() => void) | undefined;
  onReopenChat?: ((id: string) => void) | undefined;
  onReorder?:
    | ((id: string, position: number, pinned: boolean) => void)
    | undefined;
  readOnly?: boolean;
  finderHintVisible?: boolean;
  onFinderOpenChange?: ((open: boolean) => void) | undefined;
};

const DRAG_THRESHOLD = 5;
const CONTEXT_MENU_GAP = 6;

const HeaderInner = styled("div")`
  display: flex;
  align-items: center;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const Tabs = styled("div")`
  display: flex;
  gap: 8px;
  padding: 12px;
  overflow-x: auto;
  flex: 1;
  min-width: 0;

  ${mobileMedia} {
    padding: 8px;
  }

  & > .active + [data-divider],
  & > :hover + [data-divider],
  & > [data-divider]:has(+ .active),
  & > [data-divider]:has(+ :hover) {
    opacity: 0;
  }
`;

const Actions = styled("div")`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 12px 0;

  ${mobileMedia} {
    gap: 0;
    padding: 8px 8px 8px 0;
  }
`;

const LeftActions = styled("div")`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  padding: 12px 0 12px 12px;

  ${mobileMedia} {
    padding: 8px 0 8px 8px;
  }
`;

const Divider = styled("span")`
  align-self: center;
  width: 0.5px;
  height: 16px;
  flex-shrink: 0;
  background: var(--border-alpha);
  margin: 0 -4.25px;
`;

const TabSlot = styled("div")`
  position: relative;
  display: inline-flex;
  flex: 0 1 auto;
  max-width: 220px;
  min-width: 60px;
  height: 32px;
  align-items: center;
  border-radius: 24px;
  background: transparent;

  ${mobileMedia} {
    flex-shrink: 0;
    min-width: 120px;
    height: 40px;
  }

  &:hover,
  &.active {
    background: var(--alpha4);
  }

  &[data-ghost="true"] {
    background: var(--alpha4);
  }
  &[data-ghost="true"] > * {
    visibility: hidden;
  }

  &[data-pinned="true"] {
    min-width: 0;
  }

  &[data-floating="true"] {
    background: var(--surface-primary);
    outline: 0.5px solid var(--border-alpha);
    box-shadow: var(--elevation-low);
    transform: scale(1.03) rotate(-1deg);
    transition:
      transform 0.3s cubic-bezier(0.2, 0, 0, 1),
      box-shadow 0.3s cubic-bezier(0.2, 0, 0, 1);
  }
  &[data-floating="true"][data-resting="true"] {
    transform: none;
    box-shadow: none;
  }
`;

const TabLink = styled(Link)`
  position: relative;
  display: inline-flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 6px;
  height: 100%;
  box-sizing: border-box;
  padding: 0 28px 0 12px;
  border: none;
  border-radius: 24px;
  text-decoration: none;
  outline: none;
  cursor: pointer;
  color: var(--text-primary);
  background: transparent;
  user-select: none;

  ${mobileMedia} {
    padding-right: 40px;

    &[data-pinned="true"] {
      min-width: 40px;
    }
  }

  &[data-pinned="true"] {
    padding: 0 8px;
    gap: 0;
    justify-content: center;
  }
`;

const CloseButton = styled(PressButton)`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  aspect-ratio: 1;
  padding: 0;
  border: none;
  border-radius: 50%;
  color: var(--text-secondary);
  background: transparent;
  cursor: pointer;

  ${mobileMedia} {
    right: 4px;
    width: 32px;
    height: 32px;
  }

  &:hover {
    color: var(--text-primary);
    background: var(--alpha8);
  }
`;

const iconButtonStyles = `
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  color: var(--text-primary);
  background: transparent;
  cursor: pointer;

  ${mobileMedia} {
    width: 40px;
    height: 40px;
  }

  &:hover {
    background: var(--alpha4);
  }
`;

const IconButton = styled(PressButton)`
  ${iconButtonStyles}
`;

// The dropdown opener owns its own click handling, so this is a non-interactive
// box, not a nested button.
const IconButtonBox = styled("div")`
  ${iconButtonStyles}
`;

function TabIcon({ id, pinned }: { id: string; pinned?: boolean }) {
  const color = pickChatIconColor(id);
  if (pinned) {
    return (
      <span
        style={{
          position: "relative",
          display: "inline-block",
          width: 16,
          height: 16,
          flexShrink: 0,
        }}
      >
        <Icon
          name="ask-filled"
          size={16}
          color={`var(--${color}-400)`}
          style={{ position: "absolute", top: 0, left: 0 }}
        />
        <Icon
          name="ask"
          size={16}
          color={`var(--${color}-600)`}
          style={{ position: "absolute", top: 0, left: 0 }}
        />
      </span>
    );
  }
  return (
    <Icon
      name="ask"
      size={16}
      color={`var(--${color}-600)`}
      style={{ flexShrink: 0 }}
    />
  );
}

export function ChatTabBar({
  tabs,
  activeTabId,
  userId,
  closedChats,
  finderHintVisible,
  onFinderOpenChange,
  onClose,
  onRename,
  onFork,
  onDownload,
  onTogglePin,
  onNewTab,
  onTidyUp,
  onCloseAll,
  onShowHistory,
  onReopenChat,
  onReorder,
  readOnly,
}: Props) {
  const makeUrl = useMakeUrl();
  const isMobile = useIsMobile();
  const reorderable = tabs.filter((t) => !t.pending);
  const pendingTabs = tabs.filter((t) => t.pending);

  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState(-1);
  const [dropping, setDropping] = useState(false);
  const [lifting, setLifting] = useState(false);
  const [pendingDragId, setPendingDragId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    id: string;
    left: number;
    top: number;
  } | null>(null);

  const tabRefs = useRef<Map<string, HTMLElement>>(new Map());
  const pendingDrag = useRef<{ id: string; x: number; y: number } | null>(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const [originRect, setOriginRect] = useState<DOMRect | null>(null);
  const centers = useRef<{ x: number; y: number; index: number }[]>([]);
  const floatingRef = useRef<HTMLDivElement>(null);
  const draggedIdRef = useRef<string | null>(null);
  const dropIndexRef = useRef(-1);
  const initialDropIdx = useRef(-1);
  const flipSnapshots = useRef<Map<string, DOMRect>>(new Map());

  const reorderableRef = useRef(reorderable);
  const onReorderRef = useRef(onReorder);
  useEffect(() => {
    reorderableRef.current = reorderable;
    onReorderRef.current = onReorder;
  });

  const setTabRef = useCallback((id: string, el: HTMLElement | null) => {
    if (el) tabRefs.current.set(id, el);
    else tabRefs.current.delete(id);
  }, []);

  const startDrag = useCallback((id: string, x: number, y: number) => {
    const el = tabRefs.current.get(id);
    if (!el) return null;
    playClick();
    dragStart.current = { x, y };
    const rect = el.getBoundingClientRect();
    setOriginRect(rect);
    centers.current = reorderableRef.current
      .filter((t) => t.id !== id)
      .map((t, i) => {
        const ref = tabRefs.current.get(t.id);
        if (!ref) return { x: 0, y: 0, index: i };
        const rect = ref.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
          index: i,
        };
      });
    const idx = reorderableRef.current.findIndex((t) => t.id === id);
    draggedIdRef.current = id;
    dropIndexRef.current = idx;
    initialDropIdx.current = idx;
    setDropIndex(idx);
    setLifting(true);
    setDraggedId(id);
    requestAnimationFrame(() => setLifting(false));
    return rect;
  }, []);

  const handlePointerDown = (e: React.PointerEvent, id: string) => {
    if (e.pointerType === "touch" || e.button !== 0 || !onReorderRef.current)
      return;
    pendingDrag.current = { id, x: e.clientX, y: e.clientY };
    setPendingDragId(id);
  };

  useEffect(() => {
    if (!pendingDragId) return;

    const onMove = (e: PointerEvent) => {
      // `startDrag` seeds `originRect` via state, which this closure won't see
      // until the next render, so use the rect it returns for the same event
      // that crosses the threshold.
      let rect = originRect;
      if (!draggedIdRef.current) {
        const pd = pendingDrag.current;
        if (!pd) return;
        const dx = e.clientX - pd.x;
        const dy = e.clientY - pd.y;
        if (dx * dx + dy * dy < DRAG_THRESHOLD * DRAG_THRESHOLD) return;
        rect = startDrag(pd.id, pd.x, pd.y);
      }
      if (!draggedIdRef.current || !rect) return;

      if (floatingRef.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        floatingRef.current.style.left = `${rect.left + dx}px`;
        floatingRef.current.style.top = `${rect.top + dy}px`;
      }

      if (centers.current.length === 0) return;

      const cx = e.clientX;
      let nearestI = centers.current[0]!.index;
      let nearestDist = Number.POSITIVE_INFINITY;
      let nearestCenter = centers.current[0]!;
      for (const c of centers.current) {
        const ddx = cx - c.x;
        const ddy = e.clientY - c.y;
        const d = ddx * ddx + ddy * ddy;
        if (d < nearestDist) {
          nearestDist = d;
          nearestI = c.index;
          nearestCenter = c;
        }
      }
      let idx = nearestI;
      if (cx > nearestCenter.x) idx = nearestI + 1;
      const maxIdx = reorderableRef.current.length - 1;
      if (idx > maxIdx) idx = maxIdx;
      if (idx < 0) idx = 0;

      if (idx !== dropIndexRef.current) {
        const snapshots = new Map<string, DOMRect>();
        for (const [k, el] of tabRefs.current) {
          snapshots.set(k, el.getBoundingClientRect());
        }
        flipSnapshots.current = snapshots;
        dropIndexRef.current = idx;
        setDropIndex(idx);
        playClick();
      }
    };

    const onUp = () => {
      pendingDrag.current = null;
      setPendingDragId(null);
      if (!draggedIdRef.current) return;
      const draggedEl = tabRefs.current.get(draggedIdRef.current);
      const el = floatingRef.current;
      if (draggedEl && el) {
        const rect = draggedEl.getBoundingClientRect();
        el.getBoundingClientRect();
        el.style.transition = "left 0.2s ease, top 0.2s ease";
        el.style.left = `${rect.left}px`;
        el.style.top = `${rect.top}px`;
      }
      draggedIdRef.current = null;
      setDropping(true);
    };

    const onCancel = () => {
      pendingDrag.current = null;
      setPendingDragId(null);
      if (!draggedIdRef.current) return;
      draggedIdRef.current = null;
      setOriginRect(null);
      dropIndexRef.current = -1;
      initialDropIdx.current = -1;
      flipSnapshots.current.clear();
      setDraggedId(null);
      setDropIndex(-1);
      setDropping(false);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [pendingDragId, startDrag, originRect]);

  useEffect(() => {
    if (!dropping) return;
    const timer = setTimeout(() => {
      const dragged = reorderableRef.current.find((t) => t.id === draggedId);
      const rest = reorderableRef.current.filter((t) => t.id !== draggedId);
      if (
        draggedId &&
        dragged &&
        rest.length > 0 &&
        dropIndex !== initialDropIdx.current
      ) {
        const clamped = Math.min(Math.max(0, dropIndex), rest.length);
        // Pinned tabs form a contiguous front region; landing inside it pins
        // the tab, landing past it unpins it, and the boundary keeps state.
        const pinnedInRest = rest.filter((t) => t.pinned).length;
        const newPinned =
          clamped < pinnedInRest
            ? true
            : clamped > pinnedInRest
              ? false
              : !!dragged.pinned;
        const eff = (t: TabChat) => t.position ?? 0;
        const groupStart = newPinned ? 0 : pinnedInRest;
        const groupEnd = newPinned ? pinnedInRest : rest.length;
        const group = rest.slice(groupStart, groupEnd);
        const idxInGroup = Math.min(
          Math.max(clamped - groupStart, 0),
          group.length
        );
        let newPosition: number;
        if (group.length === 0) newPosition = eff(dragged);
        else if (idxInGroup <= 0) newPosition = eff(group[0]!) - 1000;
        else if (idxInGroup >= group.length)
          newPosition = eff(group[group.length - 1]!) + 1000;
        else
          newPosition =
            (eff(group[idxInGroup - 1]!) + eff(group[idxInGroup]!)) / 2;
        onReorderRef.current?.(draggedId, newPosition, newPinned);
      }
      playClick();
      setOriginRect(null);
      dropIndexRef.current = -1;
      initialDropIdx.current = -1;
      setDraggedId(null);
      setDropIndex(-1);
      setDropping(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [dropping, draggedId, dropIndex]);

  const visualOrder = reorderPreview(reorderable, draggedId, dropIndex);
  useTabFlip(visualOrder, tabRefs, flipSnapshots, !!draggedId);

  const draggedTab = draggedId
    ? reorderable.find((t) => t.id === draggedId)
    : null;
  const orderedTabs = [...visualOrder, ...pendingTabs];
  const contextTab = contextMenu
    ? (tabs.find((t) => t.id === contextMenu.id) ?? null)
    : null;
  const newChatButton = onNewTab && (
    <Tooltip content="New tab">
      <IconButton
        aria-label="New tab"
        disabled={readOnly}
        style={readOnly ? readOnlyStyle : undefined}
        onPress={onNewTab}
      >
        <Icon name="plus" size={16} />
      </IconButton>
    </Tooltip>
  );

  return (
    <HeaderInner>
      {(!readOnly || tabs.length > 0) && (
        <LeftActions>
          <ChatTabsDropdown
            opener={
              <Tooltip
                forceOpen={finderHintVisible}
                vertical={finderHintVisible ? "below" : undefined}
                contentStyle={
                  finderHintVisible
                    ? { left: "anchor(center)", translate: "-20px 0" }
                    : undefined
                }
                content={
                  finderHintVisible ? (
                    <NudgeTooltipContent data-align="left">
                      Your closed chats are still here. Search and reopen them
                      anytime.
                    </NudgeTooltipContent>
                  ) : (
                    <>
                      Search chats{" "}
                      <span
                        style={{ fontSize: 10, color: "var(--text-secondary)" }}
                      >
                        ⇧⌥C
                      </span>
                    </>
                  )
                }
              >
                <IconButtonBox>
                  <Icon name="text-search" size={16} />
                </IconButtonBox>
              </Tooltip>
            }
            tabs={tabs}
            activeTabId={activeTabId}
            userId={userId}
            closedChats={closedChats}
            onOpenChange={onFinderOpenChange}
            onReopenChat={onReopenChat}
            onNewTab={readOnly ? undefined : onNewTab}
          />
        </LeftActions>
      )}
      <Tabs>
        {(!readOnly || orderedTabs.length > 0) && (
          <Divider
            data-divider=""
            aria-hidden="true"
            style={{ marginRight: 0 }}
          />
        )}
        {orderedTabs.map((tab, i) => {
          const isActive = tab.id === activeTabId;
          const isDragged = tab.id === draggedId;
          const label = tab.name === "Untitled" ? "New tab" : tab.name;
          const link = (
            <TabLink
              to={makeUrl(routes.chats, { tabId: tab.id, userId })}
              data-pinned={tab.pinned ? "true" : undefined}
              aria-label={label}
              aria-current={isActive ? "page" : undefined}
              onPointerDown={
                tab.pending ? undefined : (e) => handlePointerDown(e, tab.id)
              }
            >
              <TabIcon id={tab.id} pinned={tab.pinned} />
              {!tab.pinned && (
                <Text variant="text-small-md" ellipsis>
                  {label}
                </Text>
              )}
            </TabLink>
          );
          return (
            <Fragment key={tab.id}>
              {i > 0 && <Divider data-divider="" aria-hidden="true" />}
              <TabSlot
                ref={(el) => setTabRef(tab.id, el)}
                className={isActive ? "active" : undefined}
                data-ghost={isDragged ? "true" : undefined}
                data-pinned={tab.pinned ? "true" : undefined}
                onContextMenu={
                  readOnly
                    ? undefined
                    : (e) => {
                        e.preventDefault();
                        playClick();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setContextMenu({
                          id: tab.id,
                          left: rect.left,
                          top: rect.bottom + CONTEXT_MENU_GAP,
                        });
                      }
                }
              >
                {tab.pinned ? <Tooltip content={label}>{link}</Tooltip> : link}
                {onClose && !tab.pinned && (
                  <CloseButton
                    aria-label="Close tab"
                    disabled={readOnly}
                    style={readOnly ? readOnlyStyle : undefined}
                    onPress={() => onClose(tab.id)}
                  >
                    <Icon name="x" size={12} />
                  </CloseButton>
                )}
              </TabSlot>
            </Fragment>
          );
        })}
        {!isMobile && onNewTab && (
          <>
            {orderedTabs.length > 0 && (
              <Divider data-divider="" aria-hidden="true" />
            )}
            {newChatButton}
          </>
        )}
      </Tabs>
      {draggedTab && originRect && (
        <div
          ref={floatingRef}
          style={{
            position: "fixed",
            left: originRect.left,
            top: originRect.top,
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          <TabSlot
            data-floating="true"
            data-resting={lifting || dropping ? "true" : undefined}
            data-pinned={draggedTab.pinned ? "true" : undefined}
          >
            <TabLink
              to={makeUrl(routes.chats, { tabId: draggedTab.id, userId })}
              data-pinned={draggedTab.pinned ? "true" : undefined}
            >
              <TabIcon id={draggedTab.id} pinned={draggedTab.pinned} />
              {!draggedTab.pinned && (
                <Text variant="text-small-md" ellipsis>
                  {draggedTab.name === "Untitled" ? "New tab" : draggedTab.name}
                </Text>
              )}
            </TabLink>
          </TabSlot>
        </div>
      )}
      {((isMobile && onNewTab) || onTidyUp || onCloseAll || onShowHistory) && (
        <Actions>
          {isMobile && newChatButton}
          {(onTidyUp || onCloseAll || onShowHistory) && (
            <Dropdown
              align="bottom"
              minWidth={180}
              opener={
                <IconButtonBox>
                  <Icon name="more" size={16} />
                </IconButtonBox>
              }
              sections={[
                {
                  items: [
                    ...(onTidyUp
                      ? [
                          {
                            type: "button" as const,
                            left: <Icon name="brush-cleaning" size={16} />,
                            label: "Tidy up",
                            onClick: onTidyUp,
                          },
                        ]
                      : []),
                    ...(onCloseAll
                      ? [
                          {
                            type: "button" as const,
                            left: <Icon name="x" size={16} />,
                            label: "Close all chats",
                            onClick: onCloseAll,
                          },
                        ]
                      : []),
                    ...(onShowHistory
                      ? [
                          {
                            type: "button" as const,
                            left: <Icon name="history" size={16} />,
                            label: "History",
                            onClick: onShowHistory,
                          },
                        ]
                      : []),
                  ],
                },
              ]}
            />
          )}
        </Actions>
      )}
      {contextMenu && contextTab && (
        <ChatTabContextMenu
          left={contextMenu.left}
          top={contextMenu.top}
          pinned={contextTab.pinned}
          onTogglePin={
            onTogglePin && contextTab.started
              ? () => onTogglePin(contextTab.id)
              : undefined
          }
          onRename={
            onRename && !contextTab.pending
              ? () => onRename(contextTab.id)
              : undefined
          }
          onFork={
            onFork && contextTab.started
              ? () => onFork(contextTab.id)
              : undefined
          }
          onDownload={
            onDownload && contextTab.started
              ? () => onDownload(contextTab.id)
              : undefined
          }
          onClose={onClose ? () => onClose(contextTab.id) : undefined}
          onCloseMenu={() => setContextMenu(null)}
        />
      )}
    </HeaderInner>
  );
}

function reorderPreview(
  ordered: readonly TabChat[],
  draggedId: string | null,
  dropIndex: number
): readonly TabChat[] {
  if (!draggedId || dropIndex < 0) return ordered;
  const dragged = ordered.find((t) => t.id === draggedId);
  if (!dragged) return ordered;
  const rest = ordered.filter((t) => t.id !== draggedId);
  const clamped = Math.min(Math.max(0, dropIndex), rest.length);
  const next = rest.slice();
  next.splice(clamped, 0, dragged);
  return next;
}

function useTabFlip(
  order: readonly TabChat[],
  tabRefs: React.MutableRefObject<Map<string, HTMLElement>>,
  snapshotsRef: React.MutableRefObject<Map<string, DOMRect>>,
  draggingActive: boolean
) {
  useLayoutEffect(() => {
    if (!draggingActive) {
      for (const el of tabRefs.current.values()) {
        el.style.transform = "";
        el.style.transition = "";
      }
      snapshotsRef.current.clear();
      return;
    }

    const snapshots = snapshotsRef.current;
    if (snapshots.size === 0) return;

    for (const t of order) {
      const el = tabRefs.current.get(t.id);
      if (!el) continue;
      const prev = snapshots.get(t.id);
      if (!prev) continue;

      el.style.transition = "none";
      el.style.transform = "";
      const layout = el.getBoundingClientRect();
      const dx = prev.left - layout.left;
      const dy = prev.top - layout.top;
      if (dx === 0 && dy === 0) continue;

      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.getBoundingClientRect();
      el.style.transition = "transform 200ms ease";
      el.style.transform = "";
    }

    snapshots.clear();
  }, [order, tabRefs, snapshotsRef, draggingActive]);
}
